import React, { useCallback, useId } from 'react'
import { RotateCcw, Percent, Layers } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TickerInput } from './TickerInput'
import { DateRangeSelector } from './DateRangeSelector'
import { Button } from '@/components/ui/Button'
import { ColumnToggle, type ColumnDefinition } from '@/components/ui/ColumnToggle'

interface ParameterBarProps {
  tickers: string[]
  onTickersChange: (tickers: string[]) => void
  onTickerAdd?: (ticker: string) => void
  startDate: string
  endDate: string
  onStartDateChange: (date: string) => void
  onEndDateChange: (date: string) => void
  initialInvestment: number
  onInitialInvestmentChange: (amount: number) => void
  adjustInflation: boolean
  onAdjustInflationChange: (value: boolean) => void
  groupByUnderlying: boolean
  onGroupByUnderlyingChange: (value: boolean) => void
  onReset: () => void
  isCalculating: boolean
  className?: string
  disabled?: boolean
  // Column toggle props
  columns: ColumnDefinition[]
  visibleColumns: string[]
  onColumnToggle: (id: string) => void
  onColumnToggleAll: (show: boolean) => void
  // Optional: hide the group by underlying button (for ShowBy page which has its own grouping)
  hideGroupByUnderlying?: boolean
}

const DEFAULT_INITIAL_INVESTMENT = 10000
const MIN_INVESTMENT = 0
const MAX_INVESTMENT = 100000000 // 100 million

/**
 * ParameterBar - A compact horizontal bar combining all calculator inputs
 * Layout: [Ticker Input] | [$Amount] | [Start] | [End] | [Inflation] | [Columns] | [Reset]
 */
export function ParameterBar({
  tickers,
  onTickersChange,
  onTickerAdd,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  initialInvestment,
  onInitialInvestmentChange,
  adjustInflation,
  onAdjustInflationChange,
  groupByUnderlying,
  onGroupByUnderlyingChange,
  onReset,
  isCalculating,
  className,
  disabled = false,
  // Column toggle props
  columns,
  visibleColumns,
  onColumnToggle,
  onColumnToggleAll,
  hideGroupByUnderlying = false,
}: ParameterBarProps) {
  const idPrefix = useId()

  // Handle initial investment change
  const handleInitialInvestmentChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value

      // Allow empty input for easier editing
      if (value === '') {
        onInitialInvestmentChange(0)
        return
      }

      // Parse the value
      const numValue = parseFloat(value)

      if (!isNaN(numValue) && numValue >= MIN_INVESTMENT && numValue <= MAX_INVESTMENT) {
        onInitialInvestmentChange(numValue)
      }
    },
    [onInitialInvestmentChange]
  )

  // Handle blur to reset to default if empty
  const handleInitialInvestmentBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (e.target.value === '' || initialInvestment === 0) {
        onInitialInvestmentChange(DEFAULT_INITIAL_INVESTMENT)
      }
    },
    [initialInvestment, onInitialInvestmentChange]
  )

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3 p-3',
        'bg-white dark:bg-gray-900',
        'border border-gray-200 dark:border-gray-700 rounded-lg',
        'shadow-sm',
        className
      )}
    >
      {/* Ticker Input */}
      <div className="flex-shrink-0 min-w-[180px] max-w-[250px]">
        <TickerInput
          value={tickers}
          onChange={onTickersChange}
          onTickerAdd={onTickerAdd}
          disabled={disabled || isCalculating}
          compact
        />
      </div>

      {/* Divider */}
      <div className="hidden sm:block w-px h-8 bg-gray-200 dark:bg-gray-700" />

      {/* Initial Investment Input */}
      <div className="flex-shrink-0">
        <div className="relative">
          <span
            className={cn(
              'absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-gray-500 dark:text-gray-400',
              (disabled || isCalculating) && 'text-gray-400 dark:text-gray-500'
            )}
            aria-hidden="true"
          >
            $
          </span>
          <input
            id={`${idPrefix}-investment`}
            type="number"
            value={initialInvestment || ''}
            onChange={handleInitialInvestmentChange}
            onBlur={handleInitialInvestmentBlur}
            min={MIN_INVESTMENT}
            max={MAX_INVESTMENT}
            step={100}
            placeholder="10,000"
            disabled={disabled || isCalculating}
            title="Initial Investment"
            className={cn(
              'w-[100px] pl-6 pr-2 py-1.5 text-sm border rounded-lg',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
              'disabled:bg-gray-100 disabled:cursor-not-allowed dark:disabled:bg-gray-800',
              'dark:bg-gray-900 dark:border-gray-700 dark:text-white',
              'placeholder:text-gray-400 dark:placeholder:text-gray-500',
              '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
            )}
            aria-label="Initial investment amount"
          />
        </div>
      </div>

      {/* Divider */}
      <div className="hidden sm:block w-px h-8 bg-gray-200 dark:bg-gray-700" />

      {/* Date Range */}
      <DateRangeSelector
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        disabled={disabled || isCalculating}
        compact
      />

      {/* Divider */}
      <div className="hidden sm:block w-px h-8 bg-gray-200 dark:bg-gray-700" />

      {/* Inflation Toggle Button */}
      <button
        type="button"
        onClick={() => onAdjustInflationChange(!adjustInflation)}
        disabled={disabled || isCalculating}
        title={`Adjust for Inflation: ${adjustInflation ? 'On' : 'Off'}`}
        aria-pressed={adjustInflation}
        className={cn(
          'inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors duration-200',
          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          adjustInflation
            ? 'bg-blue-600 text-white hover:bg-blue-700'
            : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-700'
        )}
      >
        <Percent className="w-4 h-4" aria-hidden="true" />
        <span className="sr-only">
          {adjustInflation ? 'Disable' : 'Enable'} Inflation Adjustment
        </span>
      </button>

      {/* Group by Underlying Toggle Button */}
      {!hideGroupByUnderlying && (
        <button
          type="button"
          onClick={() => onGroupByUnderlyingChange(!groupByUnderlying)}
          disabled={disabled || isCalculating}
          title={`Group by Underlying: ${groupByUnderlying ? 'On' : 'Off'}`}
          aria-pressed={groupByUnderlying}
          className={cn(
            'inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            groupByUnderlying
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:bg-gray-700'
          )}
        >
          <Layers className="w-4 h-4" aria-hidden="true" />
          <span className="sr-only">
            {groupByUnderlying ? 'Disable' : 'Enable'} Group by Underlying
          </span>
        </button>
      )}

      {/* Divider */}
      <div className="hidden sm:block w-px h-8 bg-gray-200 dark:bg-gray-700" />

      {/* Action Buttons */}
      <div className="flex items-center gap-2 ml-auto">
        {/* Column Toggle */}
        <ColumnToggle
          columns={columns}
          visibleColumns={visibleColumns}
          onToggle={onColumnToggle}
          onToggleAll={onColumnToggleAll}
        />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onReset}
          disabled={disabled || isCalculating}
          title="Reset all parameters"
          leftIcon={<RotateCcw className="w-4 h-4" />}
        >
          <span className="sr-only sm:not-sr-only">Reset</span>
        </Button>
      </div>
    </div>
  )
}

export default ParameterBar
